import type { RecorderConfig } from '../types';

export interface BrowserSupport {
  supported: boolean;
  features: {
    mediaRecorder: boolean;
    getUserMedia: boolean;
    webrtc: boolean;
    indexedDB: boolean;
    serviceWorker: boolean;
  };
  recommendations?: string[];
  deviceType: 'mobile' | 'desktop' | 'tablet';
  browserInfo: {
    name: string;
    version: string;
    engine: string;
  };
}

type WinWithVendors = Window & {
  webkitRTCPeerConnection?: unknown;
  RTCPeerConnection?: unknown;
};

export class BrowserSupportChecker {
  static async checkSupport(): Promise<BrowserSupport> {
    const hasWindow = typeof window !== 'undefined';
    const hasNavigator = typeof navigator !== 'undefined';

    const features = {
      mediaRecorder: hasWindow && 'MediaRecorder' in window,
      getUserMedia:
          hasNavigator &&
          !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      webrtc:
          hasWindow &&
          !!(
              (window as WinWithVendors).RTCPeerConnection ||
              (window as WinWithVendors).webkitRTCPeerConnection
          ),
      indexedDB: hasWindow && 'indexedDB' in window,
      serviceWorker: hasNavigator && 'serviceWorker' in navigator
    };

    const supported = features.mediaRecorder && features.getUserMedia;
    const recommendations: string[] = [];

    if (!features.mediaRecorder) {
      recommendations.push(
          'MediaRecorder API not supported. Consider using a WebRTC-based fallback.'
      );
    }

    if (!features.getUserMedia) {
      recommendations.push('getUserMedia not supported. Camera access unavailable.');
    }

    if (!features.webrtc) {
      recommendations.push('WebRTC not supported. Real-time features may be limited.');
    }

    if (!features.indexedDB) {
      recommendations.push('IndexedDB not supported. Offline capabilities unavailable.');
    }

    const deviceType = this.detectDeviceType();
    const browserInfo = this.getBrowserInfo();

    return recommendations.length
        ? { supported, features, recommendations, deviceType, browserInfo }
        : { supported, features, deviceType, browserInfo };
  }

  static detectDeviceType(): 'mobile' | 'desktop' | 'tablet' {
    if (typeof navigator === 'undefined') return 'desktop';
    const ua = navigator.userAgent.toLowerCase();

    // iPadOS can report as Mac; detect touch-capable Macs as tablets
    const isIPadOS =
        /\biPad\b/.test(navigator.userAgent) ||
        (/\bMacintosh\b/.test(navigator.userAgent) &&
            'ontouchend' in (typeof window !== 'undefined' ? window : ({} as Window)));

    const isTablet = /ipad|android(?!.*mobile)/i.test(ua) || isIPadOS;
    const isMobile =
        /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua) &&
        !isTablet;

    if (isTablet) return 'tablet';
    if (isMobile) return 'mobile';
    return 'desktop';
  }

  static getBrowserInfo(): { name: string; version: string; engine: string } {
    if (typeof navigator === 'undefined') {
      return { name: 'Unknown', version: 'Unknown', engine: 'Unknown' };
    }

    const ua = navigator.userAgent;
    let name = 'Unknown';
    let version = 'Unknown';
    let engine = 'Unknown';

    if (ua.includes('Edg/')) {
      name = 'Edge';
      version = ua.match(/Edg\/(\d+)/)?.[1] ?? 'Unknown';
      engine = 'Blink';
    } else if (ua.includes('Chrome') && !ua.includes('Edg') && !ua.includes('OPR')) {
      name = 'Chrome';
      version = ua.match(/Chrome\/(\d+)/)?.[1] ?? 'Unknown';
      engine = 'Blink';
    } else if (ua.includes('Firefox')) {
      name = 'Firefox';
      version = ua.match(/Firefox\/(\d+)/)?.[1] ?? 'Unknown';
      engine = 'Gecko';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      name = 'Safari';
      version = ua.match(/Version\/(\d+)/)?.[1] ?? 'Unknown';
      engine = 'WebKit';
    } else if (ua.includes('OPR/') || ua.includes('Opera')) {
      name = 'Opera';
      version = ua.match(/OPR\/(\d+)/)?.[1] ?? ua.match(/Opera\/(\d+)/)?.[1] ?? 'Unknown';
      engine = 'Blink';
    }

    return { name, version, engine };
  }

  static getRecommendedSettings(
      deviceType: 'mobile' | 'desktop' | 'tablet' = 'desktop'
  ): Partial<RecorderConfig> {
    const baseSettings = {
      audioEnabled: true,
      showPreview: true,
      maxRetries: 3
    } as const;

    switch (deviceType) {
      case 'mobile':
        return {
          ...baseSettings,
          videoQuality: 'medium',
          maxDuration: 180,
          chunkSize: 2 * 1024 * 1024,
          parallelUploads: 2
        };
      case 'tablet':
        return {
          ...baseSettings,
          videoQuality: 'medium',
          maxDuration: 300,
          chunkSize: 5 * 1024 * 1024,
          parallelUploads: 3
        };
      case 'desktop':
      default:
        return {
          ...baseSettings,
          videoQuality: 'high',
          maxDuration: 600,
          chunkSize: 10 * 1024 * 1024,
          parallelUploads: 4
        };
    }
  }

  static async testCameraAccess(): Promise<{
    hasCamera: boolean;
    hasMicrophone: boolean;
    devices: MediaDeviceInfo[];
  }> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return { hasCamera: false, hasMicrophone: false, devices: [] };
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      const audioDevices = devices.filter((d) => d.kind === 'audioinput');

      return {
        hasCamera: videoDevices.length > 0,
        hasMicrophone: audioDevices.length > 0,
        devices
      };
    } catch {
      return { hasCamera: false, hasMicrophone: false, devices: [] };
    }
  }

  static async testRecordingCapability(): Promise<{
    canRecord: boolean;
    supportedMimeTypes: string[];
    maxResolution?: { width: number; height: number };
  }> {
    const hasWindow = typeof window !== 'undefined';
    const hasNavigator = typeof navigator !== 'undefined';
    const hasMediaRecorder =
        hasWindow && 'MediaRecorder' in window && typeof (window as any).MediaRecorder !== 'undefined';

    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm',
      'video/mp4;codecs=h264,aac',
      'video/mp4'
    ];

    const supportedMimeTypes = hasMediaRecorder
        ? candidates.filter((t) => (window as any).MediaRecorder.isTypeSupported?.(t))
        : [];

    let maxResolution: { width: number; height: number } | undefined;

    if (hasNavigator && navigator.mediaDevices?.getUserMedia) {
      try {
        // Try 1080p
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 1920, height: 1080 }
        });
        const videoTrack = stream.getVideoTracks()[0];
        const settings = videoTrack?.getSettings?.() ?? {};
        maxResolution = {
          width: (settings.width as number | undefined) ?? 1920,
          height: (settings.height as number | undefined) ?? 1080
        };
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        // Fallback to 720p
        maxResolution = { width: 1280, height: 720 };
      }
    }

    return {
      canRecord: supportedMimeTypes.length > 0,
      supportedMimeTypes,
      maxResolution
    };
  }
}
